import React, { useContext, useMemo } from "react";
import { Alert, Box, Button, Grid, Link, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  formTitleStyles,
  locationStyle,
} from "../component/DeliveryInformationForm/UserDetailInfo/UserDetailInfo";
import {
  AddressConfirmStatusEnum,
  RequestStatusEnum,
  StringFulBooleanValue,
} from "../../../helper/constant";
import _ from "lodash";
import { AssetWithState } from "../../AssetList/AssetList";
import { UseUpdateReplacementRequest } from "../async/useUpdateReplacementRequest";
import { UpdateReplacementRequestBody } from "../../../resource/bff";
import { AssetReplacementListContext } from "../../HardwareDetails/HardwareDetails";
import CancelButton from "../component/CancelButton/CancelButton";
import CloseIconButton from "../component/CloseIconButton/CloseIconButton";

const formSubTitleStyles = {
  fontFamily: "Inter",
  fontWeight: "400",
  fontSize: "16px",
};

type Props = {
  currentAsset: AssetWithState;
  handleCloseModal: () => void;
  changeEditStatus: (val: boolean) => void;
  setIsAlertStatus: (val: RequestStatusEnum) => void;
  setAlertMessageOpen: () => void;
  setLoadingStatus: (val: boolean) => void;
};

export const confirmButtonStyle = {
  color: "#FFFFFF",
  backgroundColor: "#BD4257",
  fontSize: "14px",
  fontWight: "500",
  fontFamily: "Inter",
  padding: "6px 16px",
  "&:hover": {
    backgroundColor: "#BD4257",
  },
};

const useStyles = makeStyles({
  myAlert: {
    color: "#003D4F",
    borderColor: "#003D4F",
    "& .MuiAlert-icon": {
      color: "#003D4F",
    },
  },
});

const UserPreferenceSummary = ({
  currentAsset,
  handleCloseModal,
  changeEditStatus,
  setLoadingStatus,
  setIsAlertStatus,
  setAlertMessageOpen,
}: Props) => {
  const classes = useStyles();
  const info = currentAsset?.details;
  const { mutate } = UseUpdateReplacementRequest();
  const reconfirmStatus = useMemo(
    () =>
      currentAsset?.details?.reconfirmationStatus ===
      AddressConfirmStatusEnum.INITIATED,
    [currentAsset]
  );
  const { refetchAssetReplacements } = useContext(AssetReplacementListContext);

  const handleConfirmAddress = (asset: AssetWithState) => {
    const buildRequestBody: UpdateReplacementRequestBody = {
      requestId: asset.requestId,
      country: asset.details.country,
      city: asset.details.city,
      postalCode: asset.details.postalCode,
      state: asset.details.state,
      mailingAddress: asset.details.mailingAddress,
      contactNumber: asset.details.contactNumber,
      isBuyOldLaptop: asset.details.buyBackOption,
    };
    setLoadingStatus(true);
    mutate(buildRequestBody, {
      onSuccess: (data) => {
        refetchAssetReplacements().then(() => {
          setLoadingStatus(false);
        });
      },
      onError: (error) => {
        setLoadingStatus(false);
        setIsAlertStatus(RequestStatusEnum.FAILURE);
        setAlertMessageOpen();
      },
      onSettled: () => {},
    });
  };

  return (
    <>
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography
            id="preference-detail-modal-title"
            variant="h6"
            component="h2"
            color={"#634F7D"}
            fontFamily={"Inter"}
            fontWeight={"700"}
            fontSize={"24px"}
          >
            {reconfirmStatus
              ? "Delivery Preference Confirmation"
              : "Laptop Renewal"}
          </Typography>
          <Box sx={{ display: "flex", gap: "24px", alignItems: "center" }}>
            <CloseIconButton
              handleClick={() => {
                handleCloseModal();
              }}
            />
          </Box>
        </Box>
      </Grid>
      {reconfirmStatus && (
        <Grid item xs={12} sx={{ marginBottom: "16px" }}>
          <Alert severity="info" variant="outlined" className={classes.myAlert}>
            Please modify or confirm your delivery preference for the laptop
            renewal.
          </Alert>
        </Grid>
      )}
      <Grid item xs={12} sx={{ marginBottom: "16px" }}>
        <Typography
          id="preference-detail-modal-description"
          fontFamily={"Inter"}
          fontWeight={"600"}
          fontSize={"20px"}
        >
          Delivery Preference
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box
              sx={{
                padding: "16px",
                backgroundColor: "#EDF1F3",
                position: "relative",
              }}
            >
              {reconfirmStatus && (
                <Button
                  variant={"text"}
                  onClick={() => {
                    changeEditStatus(true);
                  }}
                  sx={{
                    position: "absolute",
                    top: "10px",
                    right: "16px",
                    color: "#BD4257",
                    padding: 0,
                    minWidth: "auto",
                    backgroundColor: "#EDF1F3",
                    "&:hover": {
                      backgroundColor: "#EDF1F3",
                    },
                  }}
                >
                  Edit
                </Button>
              )}
              <Box sx={{ marginBottom: "12px" }}>
                <Typography variant="h6" component="h2" sx={formTitleStyles}>
                  Preferred Mailing Address
                </Typography>
                <Box>
                  <p style={locationStyle}>{info?.mailingAddress}</p>
                  <p style={locationStyle}>
                    {info?.city} - {info?.postalCode}
                  </p>
                  <p style={locationStyle}>{info?.state}</p>
                  <p style={locationStyle}>{info?.country}</p>
                </Box>
              </Box>
              <Box>
                <Typography variant="h6" component="h2" sx={formTitleStyles}>
                  Preferred Contact Number
                </Typography>
                <p style={locationStyle}>{info?.contactNumber}</p>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>
      {currentAsset?.isBuybackPreferenceEnabled ===
        StringFulBooleanValue.TRUE &&
        !reconfirmStatus && (
          <>
            <Grid item xs={12} sx={{ marginBottom: "16px", marginTop: "16px" }}>
              <Typography
                id="preference-detail-description"
                fontFamily={"Inter"}
                fontWeight={"600"}
                fontSize={"20px"}
              >
                Buyback Preference
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ padding: "16px", backgroundColor: "#EDF1F3" }}>
                <Box sx={{ marginBottom: "12px" }}>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={formTitleStyles}
                    marginBottom={"12px"}
                  >
                    End Of Life Model:{" "}
                    <span style={{ fontWeight: 400 }}>
                      {currentAsset.modelName}
                    </span>
                  </Typography>

                  <Link
                    target="_blank"
                    href={currentAsset.buyBackLink}
                    color="#BD4257;"
                    underline="hover"
                    marginBottom={"12px"}
                  >
                    Check the price
                  </Link>
                </Box>
                <Box>
                  <Typography component="div" sx={formSubTitleStyles}>
                    <Typography
                      variant="h6"
                      component="h2"
                      sx={formTitleStyles}
                      marginBottom={"12px"}
                    >
                      Buying back old laptop:{" "}
                      <span style={{ fontWeight: 400 }}>
                        {_.capitalize(info?.buyBackOption)}
                      </span>
                    </Typography>
                    Refer to the{" "}
                    <Link
                      target="_blank"
                      href="https://thoughtworks.atlassian.net/wiki/spaces/POLICY/pages/271384608/Laptop+policy+-+Global"
                      color="#BD4257"
                      underline="hover"
                    >
                      Global Laptop Policy
                    </Link>
                    .
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </>
        )}
      {reconfirmStatus ? (
        <Grid
          container
          direction="row"
          justifyContent="flex-end"
          alignItems="flex-end"
          gap="32px"
          sx={{
            marginTop: "48px",
          }}
        >
          <CancelButton
            handleClick={() => {
              handleCloseModal();
            }}
          />
          <Button
            variant={"outlined"}
            sx={confirmButtonStyle}
            onClick={() => handleConfirmAddress(currentAsset)}
          >
            Confirm
          </Button>
        </Grid>
      ) : (
        <Grid
          container
          direction="row"
          justifyContent="flex-end"
          alignItems="flex-end"
          gap="32px"
          sx={{ marginTop: "48px" }}
        >
          <Button
            variant={"text"}
            onClick={() => {
              handleCloseModal();
            }}
            sx={{
              color: "#FFFFFF",
              backgroundColor: "#BD4257",
              "&:hover": {
                backgroundColor: "#BD4257",
              },
            }}
          >
            Close
          </Button>
        </Grid>
      )}
    </>
  );
};

export default UserPreferenceSummary;
